import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  AlertTriangle, Heart, Phone, User, Droplet, Pill, 
  Hospital, QrCode, Download, Plus, Trash2, Shield,
  Printer, Share2, CheckCircle
} from 'lucide-react';
import Layout from '../components/Layout';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const RELATIONSHIPS = ['Spouse', 'Parent', 'Child', 'Sibling', 'Friend', 'Doctor', 'Other'];
const COMMON_ALLERGIES = ['Penicillin', 'Aspirin', 'Sulfa drugs', 'NSAIDs', 'Latex', 'Peanuts', 'Shellfish', 'Eggs', 'Milk', 'Soy', 'Wheat', 'Bee stings'];
const COMMON_CONDITIONS = ['Diabetes', 'Heart Disease', 'Hypertension', 'Asthma', 'Epilepsy', 'Cancer', 'Kidney Disease', 'Liver Disease', 'HIV/AIDS', 'Alzheimer\'s', 'Parkinson\'s', 'Arthritis'];

const EmergencyProfilePage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileExists, setProfileExists] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [emergencyUrl, setEmergencyUrl] = useState(null);
  const [publicId, setPublicId] = useState(null);
  
  const [formData, setFormData] = useState({
    full_name: '',
    date_of_birth: '',
    blood_group: '',
    gender: '',
    emergency_contacts: [{ name: '', relationship: '', phone: '', is_primary: true }],
    allergies: [],
    critical_conditions: [],
    current_medications: [],
    organ_donor: false,
    insurance_info: '',
    preferred_hospital: '',
    doctor_name: '',
    doctor_phone: '',
    preferred_language: 'en',
    special_instructions: ''
  });
  
  const [newAllergy, setNewAllergy] = useState('');
  const [newCondition, setNewCondition] = useState('');
  const [newMedication, setNewMedication] = useState('');

  const tt = (en, hi) => language === 'hi' ? hi : en;

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/emergency-profile`);
      if (response.data.exists) {
        const profile = response.data.profile;
        setFormData({
          full_name: profile.full_name || '',
          date_of_birth: profile.date_of_birth || '',
          blood_group: profile.blood_group || '',
          gender: profile.gender || '',
          emergency_contacts: profile.emergency_contacts?.length > 0 
            ? profile.emergency_contacts 
            : [{ name: '', relationship: '', phone: '', is_primary: true }],
          allergies: profile.allergies || [],
          critical_conditions: profile.critical_conditions || [],
          current_medications: profile.current_medications || [],
          organ_donor: profile.organ_donor || false,
          insurance_info: profile.insurance_info || '',
          preferred_hospital: profile.preferred_hospital || '',
          doctor_name: profile.doctor_name || '',
          doctor_phone: profile.doctor_phone || '',
          preferred_language: profile.preferred_language || 'en',
          special_instructions: profile.special_instructions || ''
        });
        setQrCode(profile.qr_code);
        setEmergencyUrl(profile.emergency_url);
        setPublicId(profile.public_id);
        setProfileExists(true);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.blood_group) {
      toast.error(tt('Please fill in required fields', 'कृपया आवश्यक फ़ील्ड भरें'));
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/emergency-profile`, formData);
      setQrCode(response.data.qr_code);
      setEmergencyUrl(response.data.emergency_url);
      setPublicId(response.data.public_id);
      setProfileExists(true);
      toast.success(tt('Emergency profile saved!', 'आपातकालीन प्रोफ़ाइल सहेजा गया!'));
    } catch (error) {
      toast.error(tt('Failed to save profile', 'प्रोफ़ाइल सहेजने में विफल'));
    } finally {
      setSaving(false);
    }
  };

  const addEmergencyContact = () => {
    if (formData.emergency_contacts.length < 3) {
      setFormData({
        ...formData,
        emergency_contacts: [...formData.emergency_contacts, { name: '', relationship: '', phone: '', is_primary: false }]
      });
    }
  };

  const removeEmergencyContact = (index) => {
    const updated = formData.emergency_contacts.filter((_, i) => i !== index);
    if (updated.length > 0 && !updated.some(c => c.is_primary)) {
      updated[0].is_primary = true;
    }
    setFormData({ ...formData, emergency_contacts: updated });
  };

  const updateContact = (index, field, value) => {
    const updated = [...formData.emergency_contacts];
    updated[index] = { ...updated[index], [field]: value };
    
    if (field === 'is_primary' && value) {
      updated.forEach((c, i) => {
        if (i !== index) c.is_primary = false;
      });
    }
    
    setFormData({ ...formData, emergency_contacts: updated });
  };

  const addItem = (field, value, setter) => {
    if (value.trim() && !formData[field].includes(value.trim())) {
      setFormData({ ...formData, [field]: [...formData[field], value.trim()] });
      setter('');
    }
  };

  const removeItem = (field, index) => {
    setFormData({
      ...formData,
      [field]: formData[field].filter((_, i) => i !== index)
    });
  };

  const downloadQRCode = () => {
    if (qrCode) {
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${qrCode}`;
      link.download = `EmergencyCard_QR_${publicId}.png`;
      link.click();
    }
  };

  const printCard = () => {
    if (publicId) {
      window.open(`${API_URL}/emergency-card/${publicId}/pdf`, '_blank');
    }
  };

  const shareProfile = async () => {
    if (emergencyUrl) {
      if (navigator.share) {
        await navigator.share({
          title: 'EmergencyCard Health Profile',
          text: tt('My emergency medical information', 'मेरी आपातकालीन चिकित्सा जानकारी'),
          url: emergencyUrl
        });
      } else {
        navigator.clipboard.writeText(emergencyUrl);
        toast.success(tt('Link copied!', 'लिंक कॉपी हो गया!'));
      }
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin w-8 h-8 border-4 border-#E63333 border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 pb-24">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-[#FEE2E2]">
            <AlertTriangle className="h-6 w-6 text-[#D92B2B]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#C02424] font-['Manrope']">
              {tt('Emergency Profile', 'आपातकालीन प्रोफ़ाइल')}
            </h1>
            <p className="text-slate-500 text-sm">
              {tt('Critical info accessible via QR code', 'QR कोड से सुलभ महत्वपूर्ण जानकारी')}
            </p>
          </div>
        </div>

        {/* QR Code Display (if profile exists) */}
        {profileExists && qrCode && (
          <Card className="border-2 border-[#FECACA] bg-[#FEF2F2]">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row items-center gap-4">
                <div className="bg-white p-3 rounded-lg shadow-sm">
                  <img 
                    src={`data:image/png;base64,${qrCode}`} 
                    alt="Emergency QR Code" 
                    className="w-32 h-32"
                  />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="font-semibold text-green-700">
                      {tt('Profile Active', 'प्रोफ़ाइल सक्रिय')}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 mb-3">
                    {tt('Anyone can scan this QR code to see your emergency info', 
                        'कोई भी इस QR कोड को स्कैन करके आपकी आपातकालीन जानकारी देख सकता है')}
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                    <Button size="sm" variant="outline" onClick={downloadQRCode} className="border-#FCA5A5">
                      <Download className="h-4 w-4 mr-1" />
                      {tt('Download QR', 'QR डाउनलोड करें')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={printCard} className="border-#FCA5A5">
                      <Printer className="h-4 w-4 mr-1" />
                      {tt('Print Card', 'कार्ड प्रिंट करें')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={shareProfile} className="border-#FCA5A5">
                      <Share2 className="h-4 w-4 mr-1" />
                      {tt('Share', 'शेयर करें')}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-[#D92B2B]" />
                {tt('Basic Information', 'बुनियादी जानकारी')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{tt('Full Name', 'पूरा नाम')} *</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder={tt('As on ID card', 'जैसा पहचान पत्र पर है')}
                  required
                  data-testid="emergency-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{tt('Date of Birth', 'जन्म तिथि')} *</Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    required
                    data-testid="emergency-dob-input"
                  />
                </div>
                <div>
                  <Label>{tt('Blood Group', 'रक्त समूह')} *</Label>
                  <Select
                    value={formData.blood_group}
                    onValueChange={(value) => setFormData({ ...formData, blood_group: value })}
                  >
                    <SelectTrigger data-testid="emergency-blood-select">
                      <SelectValue placeholder={tt('Select', 'चुनें')} />
                    </SelectTrigger>
                    <SelectContent>
                      {BLOOD_GROUPS.map(bg => (
                        <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>{tt('Gender', 'लिंग')}</Label>
                <Select
                  value={formData.gender}
                  onValueChange={(value) => setFormData({ ...formData, gender: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={tt('Select', 'चुनें')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">{tt('Male', 'पुरुष')}</SelectItem>
                    <SelectItem value="female">{tt('Female', 'महिला')}</SelectItem>
                    <SelectItem value="other">{tt('Other', 'अन्य')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Emergency Contacts */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Phone className="h-5 w-5 text-[#D92B2B]" />
                {tt('Emergency Contacts', 'आपातकालीन संपर्क')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {formData.emergency_contacts.map((contact, index) => (
                <div key={index} className="p-3 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {tt('Contact', 'संपर्क')} {index + 1}
                      {contact.is_primary && (
                        <Badge className="ml-2 bg-[#E63333]">
                          {tt('Primary', 'प्राथमिक')}
                        </Badge>
                      )}
                    </span>
                    {formData.emergency_contacts.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeEmergencyContact(index)}
                        className="text-[#E63333]"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  
                  <Input
                    placeholder={tt('Name', 'नाम')}
                    value={contact.name}
                    onChange={(e) => updateContact(index, 'name', e.target.value)}
                  />
                  
                  <div className="grid grid-cols-2 gap-2">
                    <Select
                      value={contact.relationship}
                      onValueChange={(value) => updateContact(index, 'relationship', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={tt('Relationship', 'रिश्ता')} />
                      </SelectTrigger>
                      <SelectContent>
                        {RELATIONSHIPS.map(rel => (
                          <SelectItem key={rel} value={rel.toLowerCase()}>{rel}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder={tt('Phone', 'फोन')}
                      value={contact.phone}
                      onChange={(e) => updateContact(index, 'phone', e.target.value)}
                    />
                  </div>
                  
                  {!contact.is_primary && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => updateContact(index, 'is_primary', true)}
                      className="text-xs"
                    >
                      {tt('Set as Primary', 'प्राथमिक बनाएं')}
                    </Button>
                  )}
                </div>
              ))}
              
              {formData.emergency_contacts.length < 3 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={addEmergencyContact}
                  className="w-full border-dashed"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {tt('Add Contact', 'संपर्क जोड़ें')}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Allergies */}
          <Card className="border-[#FECACA]">
            <CardHeader className="pb-3 bg-[#FEF2F2] rounded-t-lg">
              <CardTitle className="text-lg flex items-center gap-2 text-[#C02424]">
                <AlertTriangle className="h-5 w-5" />
                {tt('Allergies', 'एलर्जी')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              <div className="flex flex-wrap gap-2">
                {formData.allergies.map((allergy, index) => (
                  <Badge key={index} variant="destructive" className="flex items-center gap-1">
                    {allergy}
                    <button type="button" onClick={() => removeItem('allergies', index)}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              
              <div className="flex gap-2">
                <Input
                  placeholder={tt('Add allergy...', 'एलर्जी जोड़ें...')}
                  value={newAllergy}
                  onChange={(e) => setNewAllergy(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('allergies', newAllergy, setNewAllergy))}
                />
                <Button type="button" onClick={() => addItem('allergies', newAllergy, setNewAllergy)}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-1">
                {COMMON_ALLERGIES.filter(a => !formData.allergies.includes(a)).slice(0, 6).map(allergy => (
                  <Button
                    key={allergy}
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => addItem('allergies', allergy, () => {})}
                  >
                    + {allergy}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Critical Conditions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Heart className="h-5 w-5 text-[#D92B2B]" />
                {tt('Critical Conditions', 'गंभीर स्थितियां')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {formData.critical_conditions.map((condition, index) => (
                  <Badge key={index} className="bg-orange-500 flex items-center gap-1">
                    {condition}
                    <button type="button" onClick={() => removeItem('critical_conditions', index)}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              
              <div className="flex gap-2">
                <Input
                  placeholder={tt('Add condition...', 'स्थिति जोड़ें...')}
                  value={newCondition}
                  onChange={(e) => setNewCondition(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('critical_conditions', newCondition, setNewCondition))}
                />
                <Button type="button" onClick={() => addItem('critical_conditions', newCondition, setNewCondition)}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-1">
                {COMMON_CONDITIONS.filter(c => !formData.critical_conditions.includes(c)).slice(0, 6).map(condition => (
                  <Button
                    key={condition}
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => addItem('critical_conditions', condition, () => {})}
                  >
                    + {condition}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Current Medications */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Pill className="h-5 w-5 text-blue-600" />
                {tt('Current Medications', 'वर्तमान दवाइयां')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {formData.current_medications.map((med, index) => (
                  <Badge key={index} className="bg-blue-500 flex items-center gap-1">
                    {med}
                    <button type="button" onClick={() => removeItem('current_medications', index)}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              
              <div className="flex gap-2">
                <Input
                  placeholder={tt('Add medication...', 'दवाई जोड़ें...')}
                  value={newMedication}
                  onChange={(e) => setNewMedication(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('current_medications', newMedication, setNewMedication))}
                />
                <Button type="button" onClick={() => addItem('current_medications', newMedication, setNewMedication)}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Medical Provider */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Hospital className="h-5 w-5 text-green-600" />
                {tt('Medical Provider', 'चिकित्सा प्रदाता')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{tt('Preferred Hospital', 'पसंदीदा अस्पताल')}</Label>
                <Input
                  value={formData.preferred_hospital}
                  onChange={(e) => setFormData({ ...formData, preferred_hospital: e.target.value })}
                  placeholder={tt('Hospital name', 'अस्पताल का नाम')}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{tt('Doctor Name', 'डॉक्टर का नाम')}</Label>
                  <Input
                    value={formData.doctor_name}
                    onChange={(e) => setFormData({ ...formData, doctor_name: e.target.value })}
                    placeholder="Dr."
                  />
                </div>
                <div>
                  <Label>{tt('Doctor Phone', 'डॉक्टर का फोन')}</Label>
                  <Input
                    value={formData.doctor_phone}
                    onChange={(e) => setFormData({ ...formData, doctor_phone: e.target.value })}
                    placeholder="Phone number"
                  />
                </div>
              </div>
              
              <div>
                <Label>{tt('Insurance Info', 'बीमा जानकारी')}</Label>
                <Input
                  value={formData.insurance_info}
                  onChange={(e) => setFormData({ ...formData, insurance_info: e.target.value })}
                  placeholder={tt('Policy number / Provider', 'पॉलिसी नंबर / प्रदाता')}
                />
              </div>
            </CardContent>
          </Card>

          {/* Additional Settings */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5 text-purple-600" />
                {tt('Additional Information', 'अतिरिक्त जानकारी')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>{tt('Organ Donor', 'अंग दाता')}</Label>
                  <p className="text-xs text-slate-500">
                    {tt('Registered organ donor', 'पंजीकृत अंग दाता')}
                  </p>
                </div>
                <Switch
                  checked={formData.organ_donor}
                  onCheckedChange={(checked) => setFormData({ ...formData, organ_donor: checked })}
                />
              </div>
              
              <div>
                <Label>{tt('Special Instructions', 'विशेष निर्देश')}</Label>
                <Textarea
                  value={formData.special_instructions}
                  onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
                  placeholder={tt('DNR wishes, religious preferences, etc.', 'DNR इच्छाएं, धार्मिक प्राथमिकताएं, आदि')}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <Button 
            type="submit" 
            className="w-full bg-[#D92B2B] hover:bg-[#C02424] h-12 text-lg"
            disabled={saving}
            data-testid="save-emergency-profile-btn"
          >
            {saving ? (
              <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2" />
            ) : (
              <QrCode className="h-5 w-5 mr-2" />
            )}
            {profileExists 
              ? tt('Update Emergency Profile', 'प्रोफ़ाइल अपडेट करें')
              : tt('Create Emergency QR Card', 'आपातकालीन QR कार्ड बनाएं')
            }
          </Button>
        </form>
      </div>
    </Layout>
  );
};

export default EmergencyProfilePage;
